// emissionRecordActions.js
import {
  setEmissionRecord,
  setEmissionRecords,
  setEmissionRecordSearchResults,
  setSnackbar,
} from "reduxStore/state";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Helper function to get Authorization header
const authHeader = (getState) => ({
  Authorization: `Bearer ${getState().token}`,
  "Content-Type": "application/json",
});

// Fetch a single emission record by ID
export const fetchEmissionRecord = (id) => async (dispatch, getState) => {
  try {
    const response = await fetch(`${BASE_URL}/emissions/${id}`, {
      method: "GET",
      headers: authHeader(getState),
    });

    if (!response.ok) {
      const errorMsg = await response.text();
      throw new Error(errorMsg || "Failed to fetch emission record");
    }

    const record = await response.json();
    dispatch(setEmissionRecord(record));

    return record;
  } catch (error) {
    console.error("Error fetching emission record:", error);
    dispatch(
      setSnackbar({ open: true, message: error.message, severity: "error" })
    );
    return { error: error.message };
  }
};

// Fetch all emission records with optional filters
export const fetchEmissionRecords =
  (filters = {}) =>
  async (dispatch, getState) => {
    try {
      const query = new URLSearchParams(filters).toString();

      const response = await fetch(`${BASE_URL}/emissions?${query}`, {
        method: "GET",
        headers: authHeader(getState),
      });

      if (!response.ok) {
        const errorMsg = await response.text();
        throw new Error(errorMsg || "Failed to fetch emission records");
      }

      const data = await response.json();
      dispatch(setEmissionRecords(data));

      return data;
    } catch (error) {
      console.error("Error fetching emission records:", error);
      dispatch(
        setSnackbar({ open: true, message: error.message, severity: "error" })
      );
      return { error: error.message };
    }
  };

// Create a new emission record
export const createEmissionRecord = (values) => async (dispatch, getState) => {
  try {
    const response = await fetch(`${BASE_URL}/emissions`, {
      method: "POST",
      headers: authHeader(getState),
      body: JSON.stringify(values),
    });

    if (!response.ok) {
      const errorMsg = await response.text();
      throw new Error(errorMsg || "Failed to create emission record");
    }

    const newRecord = await response.json();
    dispatch(setEmissionRecord(newRecord));
    dispatch(
      setSnackbar({
        open: true,
        message: "Emission record created successfully",
        severity: "success",
      })
    );

    return newRecord;
  } catch (error) {
    console.error("Error creating emission record:", error);
    dispatch(
      setSnackbar({ open: true, message: error.message, severity: "error" })
    );
    return { error: error.message };
  }
};

// Update an emission record
export const updateEmissionRecord =
  ({ id, values }) =>
  async (dispatch, getState) => {
    try {
      const response = await fetch(`${BASE_URL}/emissions/${id}`, {
        method: "PATCH",
        headers: authHeader(getState),
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const errorMsg = await response.text();
        throw new Error(errorMsg || "Failed to update emission record");
      }

      const updated = await response.json();
      dispatch(setEmissionRecord(updated));
      dispatch(
        setSnackbar({
          open: true,
          message: "Emission record updated successfully",
          severity: "success",
        })
      );

      return updated;
    } catch (error) {
      console.error("Error updating emission record:", error);
      dispatch(
        setSnackbar({ open: true, message: error.message, severity: "error" })
      );
      return { error: error.message };
    }
  };

// Delete an emission record
export const deleteEmissionRecord = (id) => async (dispatch, getState) => {
  try {
    const response = await fetch(`${BASE_URL}/emissions/${id}`, {
      method: "DELETE",
      headers: authHeader(getState),
    });

    if (!response.ok) {
      const errorMsg = await response.text();
      const parsedError = JSON.parse(errorMsg);
      throw new Error(
        parsedError.message || "Failed to delete emission record"
      );
    }

    // Remove the deleted record from state
    const updatedList = getState().emissionRecords.data.filter(
      (record) => record._id !== id
    );
    dispatch(
      setEmissionRecords({ ...getState().emissionRecords, data: updatedList })
    );

    dispatch(
      setSnackbar({
        open: true,
        message: "Emission record deleted successfully",
        severity: "info",
      })
    );

    return true;
  } catch (error) {
    console.error("Error deleting emission record:", error);
    const { message } = error;
    dispatch(
      setSnackbar({
        open: true,
        message: message || "An unexpected error occurred",
        severity: "error",
      })
    );
    return { error: error.message };
  }
};

// Search emission records
export const searchEmissionRecords =
  (query, organisationId) => async (dispatch, getState) => {
    try {
      const qs = new URLSearchParams({ query });
      if (organisationId) qs.set("organisationId", organisationId);

      const response = await fetch(
        `${BASE_URL}/emissions/search?${qs.toString()}`,
        { method: "GET", headers: authHeader(getState) }
      );

      if (!response.ok) {
        const errorMsg = await response.text();
        throw new Error(errorMsg || "Failed to search emission records");
      }

      const results = await response.json();
      dispatch(setEmissionRecordSearchResults(results));
      return results;
    } catch (error) {
      console.error("Error searching emission records:", error);
      dispatch(
        setSnackbar({ open: true, message: error.message, severity: "error" })
      );
      return { error: error.message };
    }
  };
